// src/pages/SubscriberBeneficiaryDetailPage.jsx
// Route: /subscriber/beneficiaries/:patientId
//
// Features #3 (blood test reports/triage/documents) and #5 (appointments).
//
// 4 Aug 2026 — reorganised. This page used to stack every section (triage,
// documents, appointments, notes) onto one unbounded scroll, which buried the
// one thing a subscriber actually opens it for: "how is my parent doing right
// now?". Restructured onto the same hero + .tabs-bar information architecture
// the Navigator portal already uses for the equivalent screen
// (PatientDetailPage.js), with an Overview tab that summarises each section
// and links through to the full list.
//
// Three content fixes ride along, because they caused most of the perceived
// clutter: repeat triage runs of the same panel are collapsed to the latest
// (older ones behind a toggle), re-uploads of the same file are grouped by
// filename instead of repeating, and appointments are split into upcoming vs
// past and sorted rather than shown in arbitrary API order.
//
// All calls are gated server-side by SubscriberAccessGuard.canViewPatient() —
// a subscriber requesting a patientId that isn't their own linked beneficiary
// gets a 403, not data.

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SubscriberAppShell } from '../components/SubscriberLayout';
import { useSubscriber } from '../context/SubscriberContext';
import { subscriberAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import EmptyState from '../components/EmptyState';
import RiskBadge from '../components/RiskBadge';
import { useAutoTriage, TRIAGE_STAGES } from '../components/healthrecords/useAutoTriage';
import IdentityMismatchModal from '../components/healthrecords/IdentityMismatchModal';

const TAB_OVERVIEW     = 'Overview';
const TAB_REPORTS      = 'Health Reports';
const TAB_DOCUMENTS    = 'Documents';
const TAB_APPOINTMENTS = 'Appointments';
const TAB_NOTES        = 'Notes';

const APPT_STATUS_BADGE = {
  CONFIRMED: 'badge-confirmed',
  SCHEDULED: 'badge-confirmed',
  PENDING:   'badge-pending',
  COMPLETED: 'badge-active',
  CANCELLED: 'badge-inactive',
  NO_SHOW:   'badge-inactive',
};

// Sort helper — a missing/unparseable timestamp sorts last rather than
// throwing off the comparison with NaN.
function ts(value) {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function shortDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

// appointmentDate is a plain 'YYYY-MM-DD' and appointmentTime an 'HH:mm' —
// combined so upcoming/past is decided on the actual slot, not just the day.
function apptWhen(appointment) {
  if (!appointment.appointmentDate) return null;
  const iso = appointment.appointmentTime
    ? `${appointment.appointmentDate}T${appointment.appointmentTime}`
    : appointment.appointmentDate;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatApptWhen(appointment) {
  const d = apptWhen(appointment);
  if (!d) return 'Date to be confirmed';
  const day = d.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  return appointment.appointmentTime ? `${day} · ${appointment.appointmentTime}` : day;
}

function ApptStatusBadge({ status }) {
  const key = status?.toUpperCase();
  const cls = APPT_STATUS_BADGE[key] || 'badge-pending';
  return <span className={`badge ${cls}`}>{status || 'Pending'}</span>;
}

// ── Layout primitives ──────────────────────────────────────────────────────

function SectionCard({ title, children, style }) {
  return (
    <div className="card fade-in-up" style={style}>
      <div className="card-title">{title}</div>
      {children}
    </div>
  );
}

// A card on the Overview tab: title row plus a "View all →" that switches to
// the tab holding the full list.
function SummaryCard({ title, count, onViewAll, viewAllLabel, children }) {
  return (
    <div className="card fade-in-up" style={{ marginBottom: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 16 }}>
        <div className="card-title" style={{ marginBottom: 0 }}>{title}</div>
        {onViewAll && count > 0 && (
          <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={onViewAll}>
            {viewAllLabel || `View all ${count} →`}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function ListRow({ children }) {
  return <div className="sub-list-row">{children}</div>;
}

function RowMeta({ children }) {
  return <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{children}</span>;
}

// ── Content rows ───────────────────────────────────────────────────────────

function TriageBody({ report }) {
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <RiskBadge level={report.consensusRiskLevel} />
        <RowMeta>{shortDate(report.createdAt)}</RowMeta>
      </div>
      {report.claudeKeyFindings && (
        <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6 }}>
          {report.claudeKeyFindings}
        </div>
      )}
      {report.hasAcuteAlert && (
        <div style={{ color: 'var(--red)', fontWeight: 600, marginTop: 8, fontSize: 13 }}>
          ⚠️ Red flag — your Navigator has been notified
        </div>
      )}
    </>
  );
}

function AppointmentRow({ appointment }) {
  return (
    <ListRow>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
          {appointment.doctorName || 'Doctor'}
          {appointment.appointmentType ? ` — ${appointment.appointmentType}` : ''}
        </div>
        <RowMeta>{formatApptWhen(appointment)}</RowMeta>
      </div>
      <div style={{ marginTop: 6 }}>
        <ApptStatusBadge status={appointment.status} />
      </div>
    </ListRow>
  );
}

function NoteRow({ note }) {
  return (
    <ListRow>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>
          {note.authorName || 'Care Team'}
        </span>
        <RowMeta>{shortDate(note.createdAt)}</RowMeta>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6 }}>{note.content}</div>
    </ListRow>
  );
}

// ── Derived data ───────────────────────────────────────────────────────────

// The same report file gets re-uploaded over time, so a flat list repeats the
// same filename many times. Grouping by filename keeps every upload reachable
// (the count and the per-upload dates are both still shown) while collapsing
// the repetition that made this list unreadable.
function groupDocuments(documents) {
  const groups = new Map();
  documents.forEach((doc) => {
    const name = doc.fileName || doc.recordType || 'Untitled document';
    if (!groups.has(name)) groups.set(name, { name, uploads: [] });
    groups.get(name).uploads.push(doc);
  });
  const list = Array.from(groups.values());
  list.forEach((group) => {
    group.uploads.sort((a, b) => ts(b.uploadedAt) - ts(a.uploadedAt));
    group.latestAt = group.uploads[0]?.uploadedAt;
  });
  list.sort((a, b) => ts(b.latestAt) - ts(a.latestAt));
  return list;
}

export default function SubscriberBeneficiaryDetailPage() {
  const { patientId } = useParams();
  const { subscriber } = useSubscriber();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [beneficiary, setBeneficiary] = useState(null);
  const [triage, setTriage] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [appointments, setAppointments] = useState(null);
  const [notes, setNotes] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const [activeTab, setActiveTab] = useState(TAB_OVERVIEW);
  const [showEarlierReports, setShowEarlierReports] = useState(false);
  const [showEveryUpload, setShowEveryUpload] = useState(false);
  const [showPastAppointments, setShowPastAppointments] = useState(false);

  const autoTriage = useAutoTriage();

  // Bound to this subscriber+patient — matches healthRecordAPI's own
  // (patientId, recordId, ...) shape exactly, so useAutoTriage's default
  // patient-upload behavior needs no changes, just these two functions
  // swapped in via apiOverrides.
  const subscriberApiOverrides = subscriber?.subscriberId ? {
    saveExtractedText: (pId, recordId, text, identityMismatchConfirmed, identityMismatchComment) =>
      subscriberAPI.saveExtractedText(subscriber.subscriberId, pId, recordId, text,
        identityMismatchConfirmed, identityMismatchComment),
    saveTriage: (pId, recordId, triageData) =>
      subscriberAPI.saveTriage(subscriber.subscriberId, pId, recordId, triageData),
  } : {};

  const load = useCallback(async () => {
    if (!subscriber?.subscriberId) return;
    setError('');
    try {
      const [familyRes, triageRes, docsRes, apptRes, notesRes] = await Promise.all([
        subscriberAPI.beneficiaries(subscriber.subscriberId),
        subscriberAPI.triageReports(subscriber.subscriberId, patientId),
        subscriberAPI.documents(subscriber.subscriberId, patientId),
        subscriberAPI.appointments(subscriber.subscriberId, patientId),
        subscriberAPI.notes(subscriber.subscriberId, patientId),
      ]);
      // The beneficiary's own identity (name, risk, Previu Age) only exists on
      // the family list endpoint — there's no single-beneficiary GET — so the
      // hero reads it from there rather than inventing a new endpoint.
      setBeneficiary((familyRes.data || []).find(b => String(b.patientId) === String(patientId)) || null);
      setTriage(triageRes.data || []);
      setDocuments(docsRes.data || []);
      setAppointments(apptRes.data || []);
      setNotes(notesRes.data || []);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You don’t have access to this family member’s records.');
      } else {
        setError('Could not load this family member’s records. Please try again.');
        toast.error('Could not load records.');
      }
    }
  }, [subscriber?.subscriberId, patientId, toast]);

  useEffect(() => { load(); }, [load]);

  /**
   * isBloodTest=true: upload as BLOOD_TEST and run the full Claude+RF
   * triage pipeline afterward (Feature #3's "run triage" piece).
   * isBloodTest=false: plain document upload (Feature #4), no triage.
   */
  async function handleFileSelected(e, isBloodTest) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file || !subscriber?.subscriberId) return;
    // Bug fix: `uploading` used to be the only gate on the file inputs, but it
    // flips back to false as soon as runAutoTriage() *returns* — which happens
    // immediately once it hits an identity mismatch and pauses for the modal,
    // long before the user answers it. That let a second upload start while
    // the first's confirmation was still pending, wiping its pending state
    // (autoTriage.reset() below) out from under the still-open modal. Block
    // here too, not just via the disabled inputs, in case this fires anyway.
    if (uploading || autoTriage.isTriaging || autoTriage.identityMismatch) return;

    setUploading(true); setUploadError(''); autoTriage.reset();
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('recordType', isBloodTest ? 'BLOOD_TEST' : 'OTHER');
      const uploadRes = await subscriberAPI.uploadDocument(subscriber.subscriberId, patientId, formData);
      toast.success(isBloodTest ? 'Blood test uploaded — running triage…' : 'Document uploaded.');
      load(); // refresh the document list immediately, don't wait on triage

      if (isBloodTest) {
        const { status, triageResult, error: outcomeError } = await autoTriage.runAutoTriage(
          uploadRes.data, file, null, patientId, null, subscriberApiOverrides
        );
        if (triageResult) {
          toast.success('Triage complete.');
          load(); // refresh again — triage report now exists too
        } else if (status === 'AWAITING_IDENTITY_CONFIRMATION') {
          // DEF-023 follow-up: modal (rendered below, off autoTriage.identityMismatch)
          // takes over — nothing to toast yet, the pipeline is paused, not failed.
        } else if (status === 'FAILED') {
          toast.error(outcomeError || autoTriage.triageError || 'Triage failed.');
        }
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Could not upload this document.';
      setUploadError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  }

  async function handleConfirmUploadIdentity(comment) {
    const { triageResult, error: outcomeError } = await autoTriage.confirmIdentityMismatch(comment);
    if (triageResult) {
      toast.success('Triage complete.');
      load();
    } else if (outcomeError) {
      toast.error(outcomeError);
    }
  }

  function handleCancelUploadIdentity() {
    autoTriage.cancelIdentityMismatch();
    toast.error('Upload cancelled — the report was not saved.');
  }

  const loading = triage === null && !error;

  const reports = useMemo(
    () => [...(triage || [])].sort((a, b) => ts(b.createdAt) - ts(a.createdAt)),
    [triage]
  );
  const latestReport = reports[0] || null;
  const earlierReports = reports.slice(1);

  const docGroups = useMemo(() => groupDocuments(documents || []), [documents]);
  const flatDocs = useMemo(
    () => [...(documents || [])].sort((a, b) => ts(b.uploadedAt) - ts(a.uploadedAt)),
    [documents]
  );

  const { upcomingAppts, pastAppts } = useMemo(() => {
    const now = Date.now();
    const upcoming = [];
    const past = [];
    (appointments || []).forEach((a) => {
      const when = apptWhen(a);
      // An appointment with no usable date can't be promised as upcoming, so
      // it lands in past-and-other rather than sitting at the top forever.
      if (when && when.getTime() >= now) upcoming.push(a);
      else past.push(a);
    });
    upcoming.sort((a, b) => (apptWhen(a)?.getTime() || 0) - (apptWhen(b)?.getTime() || 0));
    past.sort((a, b) => (apptWhen(b)?.getTime() || 0) - (apptWhen(a)?.getTime() || 0));
    return { upcomingAppts: upcoming, pastAppts: past };
  }, [appointments]);

  const sortedNotes = useMemo(
    () => [...(notes || [])].sort((a, b) => ts(b.createdAt) - ts(a.createdAt)),
    [notes]
  );

  const fullName = beneficiary
    ? `${beneficiary.firstName || ''} ${beneficiary.lastName || ''}`.trim()
    : '';
  const initials = beneficiary
    ? `${beneficiary.firstName?.[0] || ''}${beneficiary.lastName?.[0] || ''}`.toUpperCase()
    : '👤';
  // Only the newest report decides this — a red flag on a superseded report is
  // history, and surfacing it as "active" would misrepresent current status.
  const hasRedFlag = Boolean(latestReport?.hasAcuteAlert);
  const heroRiskLevel = beneficiary?.latestRiskLevel || latestReport?.consensusRiskLevel;

  const TABS = [
    { id: TAB_OVERVIEW,     label: 'Overview' },
    { id: TAB_REPORTS,      label: `🩸 Health Reports${reports.length ? ` (${reports.length})` : ''}` },
    { id: TAB_DOCUMENTS,    label: `📄 Documents${docGroups.length ? ` (${docGroups.length})` : ''}` },
    { id: TAB_APPOINTMENTS, label: `📅 Appointments${upcomingAppts.length ? ` (${upcomingAppts.length} upcoming)` : ''}` },
    { id: TAB_NOTES,        label: `📝 Notes${sortedNotes.length ? ` (${sortedNotes.length})` : ''}` },
  ];

  // A second upload must stay blocked for as long as the first one has any
  // unresolved state — including the identity-mismatch modal, which is NOT
  // covered by autoTriage.isTriaging (that flag deliberately excludes
  // AWAITING_IDENTITY_CONFIRMATION, since it's "paused", not "actively
  // working"). Without also checking identityMismatch here, the inputs
  // re-enabled the instant the modal appeared, before the user answered it.
  const uploadBusy = uploading || autoTriage.isTriaging || Boolean(autoTriage.identityMismatch);

  const uploadControls = (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <label className="btn btn-ghost" style={{
          border: '1.5px dashed var(--brand-accent)',
          color: uploadBusy ? 'var(--text-muted)' : 'var(--brand-accent)',
          cursor: uploadBusy ? 'not-allowed' : 'pointer',
        }}>
          {uploadBusy ? 'Uploading…' : '🩸 Upload blood test (PDF) & run triage'}
          <input type="file" onChange={(e) => handleFileSelected(e, true)} disabled={uploadBusy}
            style={{ display: 'none' }} accept="application/pdf" />
        </label>
        <label className="btn btn-ghost" style={{
          color: uploadBusy ? 'var(--text-muted)' : 'var(--text-primary)',
          cursor: uploadBusy ? 'not-allowed' : 'pointer',
        }}>
          {uploadBusy ? 'Uploading…' : '📄 Upload other document'}
          <input type="file" onChange={(e) => handleFileSelected(e, false)} disabled={uploadBusy}
            style={{ display: 'none' }} accept="image/*,application/pdf" />
        </label>
      </div>
    </div>
  );

  return (
    <SubscriberAppShell breadcrumbs={[
      { label: 'My Family', to: '/subscriber/dashboard' },
      { label: fullName || 'Family member' },
    ]}>
      <style>{`
        .sub-list-row {
          padding: 13px 0;
          border-bottom: 1px solid var(--bg-subtle);
        }
        .sub-list-row:last-child { border-bottom: none; padding-bottom: 0; }
        .sub-overview-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 18px;
          align-items: start;
        }
        @media (max-width: 900px) {
          .sub-overview-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <span className="back-link fade-in-up" onClick={() => navigate('/subscriber/dashboard')}>
        ← Back to My Family
      </span>

      {error && (
        <EmptyState icon="alert" title="Something went wrong" message={error}
          action={{ label: 'Try again', onClick: load }} />
      )}

      {loading && !error && (
        <div className="loading-state">
          <div className="spinner" />
          Loading…
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Hero — answers "how is my parent doing?" without scrolling.
              Mirrors PatientDetailPage.js's .patient-hero. */}
          <div className="patient-hero fade-in-up delay-1">
            <div className="patient-avatar-lg">{initials}</div>
            <div style={{ flex: 1 }}>
              <div className="patient-name-lg">{fullName || 'Family member'}</div>
              <div className="patient-meta-lg">
                {beneficiary?.previuAge != null && <>Previu Age {beneficiary.previuAge}&nbsp;·&nbsp;</>}
                {beneficiary?.carePlanStatus === 'ACTION_PLAN_SET' ? 'Action plan set' : 'No action plan yet'}
                {beneficiary?.openLabOrders > 0 && (
                  <>&nbsp;·&nbsp;{beneficiary.openLabOrders} lab order{beneficiary.openLabOrders === 1 ? '' : 's'} in progress</>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {heroRiskLevel && <RiskBadge level={heroRiskLevel} />}
                {hasRedFlag && <span className="badge badge-inactive">⚠ Red flag active</span>}
                {upcomingAppts.length > 0 && (
                  <span className="badge badge-confirmed">
                    Next visit {shortDate(apptWhen(upcomingAppts[0]))}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Triage progress lives above the tabs — it's a long-running
              background job, so it must stay visible if the subscriber
              switches tabs while it runs. */}
          {autoTriage.isTriaging && (
            <div className="alert alert-info fade-in-up">
              <span>⏳</span> {TRIAGE_STAGES[autoTriage.stage]?.label || 'Processing…'}
            </div>
          )}
          {autoTriage.stage === 'FAILED' && autoTriage.triageError && (
            <div className="alert alert-error fade-in-up">{autoTriage.triageError}</div>
          )}
          {uploadError && <div className="alert alert-error fade-in-up">{uploadError}</div>}

          <div className="tabs-bar fade-in-up delay-2">
            {TABS.map(t => (
              <button key={t.id} type="button"
                className={`tab-btn ${activeTab === t.id ? 'active' : ''}`}
                onClick={() => setActiveTab(t.id)}>
                {t.label}
              </button>
            ))}
          </div>

          {/* ── OVERVIEW ── */}
          {activeTab === TAB_OVERVIEW && (
            <div className="sub-overview-grid">
              <SummaryCard
                title="Latest Health Report"
                count={reports.length}
                onViewAll={() => setActiveTab(TAB_REPORTS)}
                viewAllLabel={reports.length > 1 ? `View all ${reports.length} →` : 'View report →'}
              >
                {latestReport
                  ? <TriageBody report={latestReport} />
                  : <EmptyState compact icon="drop" title="No reports yet"
                      message="Upload a blood test in the Documents tab and we'll triage it for you." />}
              </SummaryCard>

              {/* Counts every appointment, not just upcoming — with nothing
                  scheduled the empty state points at the Appointments tab, so
                  the link through to it has to stay available. */}
              <SummaryCard
                title="Next Appointment"
                count={upcomingAppts.length + pastAppts.length}
                onViewAll={() => setActiveTab(TAB_APPOINTMENTS)}
              >
                {upcomingAppts.length > 0
                  ? upcomingAppts.slice(0, 2).map(a => <AppointmentRow key={a.id} appointment={a} />)
                  : <EmptyState compact icon="calendar" title="Nothing scheduled"
                      message={pastAppts.length > 0
                        ? 'No upcoming visits. Past appointments are in the Appointments tab.'
                        : 'Upcoming doctor visits will appear here.'} />}
              </SummaryCard>

              <SummaryCard
                title="Latest Care Team Note"
                count={sortedNotes.length}
                onViewAll={() => setActiveTab(TAB_NOTES)}
              >
                {sortedNotes.length > 0
                  ? <NoteRow note={sortedNotes[0]} />
                  : <EmptyState compact icon="note" title="No notes yet"
                      message="Notes from your Navigator or Doctor will appear here." />}
              </SummaryCard>

              <SummaryCard
                title="Recent Documents"
                count={docGroups.length}
                onViewAll={() => setActiveTab(TAB_DOCUMENTS)}
              >
                {docGroups.length > 0
                  ? docGroups.slice(0, 3).map(group => (
                      <ListRow key={group.name}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                          <span style={{ fontSize: 13 }}>{group.name}</span>
                          <RowMeta>{shortDate(group.latestAt)}</RowMeta>
                        </div>
                      </ListRow>
                    ))
                  : <EmptyState compact icon="document" title="No documents yet"
                      message="Uploaded reports and files will appear here." />}
              </SummaryCard>
            </div>
          )}

          {/* ── HEALTH REPORTS ── */}
          {activeTab === TAB_REPORTS && (
            <SectionCard title="🩸 Blood Test Reports & Triage">
              {reports.length === 0 && (
                <EmptyState icon="drop" title="No reports yet"
                  message="Upload a blood test in the Documents tab and we'll triage it for you."
                  action={{ label: 'Go to Documents', onClick: () => setActiveTab(TAB_DOCUMENTS) }} />
              )}
              {latestReport && (
                <>
                  <ListRow><TriageBody report={latestReport} /></ListRow>
                  {/* Repeat triage runs of the same panel say the same thing,
                      so only the newest is shown by default. */}
                  {earlierReports.length > 0 && !showEarlierReports && (
                    <div style={{ paddingTop: 14 }}>
                      <button type="button" className="btn btn-ghost btn-sm"
                        onClick={() => setShowEarlierReports(true)}>
                        Show {earlierReports.length} earlier report{earlierReports.length === 1 ? '' : 's'}
                      </button>
                    </div>
                  )}
                  {showEarlierReports && earlierReports.map(r => (
                    <ListRow key={r.id}><TriageBody report={r} /></ListRow>
                  ))}
                </>
              )}
            </SectionCard>
          )}

          {/* ── DOCUMENTS ── */}
          {activeTab === TAB_DOCUMENTS && (
            <SectionCard title="📄 Documents">
              {uploadControls}

              {docGroups.length === 0 && (
                <EmptyState icon="document" title="No documents yet"
                  message="Uploaded reports and files will appear here." />
              )}

              {docGroups.length > 0 && (
                <>
                  {showEveryUpload
                    ? flatDocs.map(d => (
                        <ListRow key={d.id}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                            <span style={{ fontSize: 13 }}>{d.fileName || d.recordType}</span>
                            <RowMeta>{shortDate(d.uploadedAt)}</RowMeta>
                          </div>
                        </ListRow>
                      ))
                    : docGroups.map(group => (
                        <ListRow key={group.name}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                            <span style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                              {group.name}
                              {group.uploads.length > 1 && (
                                <span className="badge badge-confirmed">×{group.uploads.length}</span>
                              )}
                            </span>
                            <RowMeta>
                              {group.uploads.length > 1 ? 'latest ' : ''}{shortDate(group.latestAt)}
                            </RowMeta>
                          </div>
                        </ListRow>
                      ))}

                  {flatDocs.length > docGroups.length && (
                    <div style={{ paddingTop: 14 }}>
                      <button type="button" className="btn btn-ghost btn-sm"
                        onClick={() => setShowEveryUpload(v => !v)}>
                        {showEveryUpload
                          ? `Group by file (${docGroups.length})`
                          : `Show every upload (${flatDocs.length})`}
                      </button>
                    </div>
                  )}
                </>
              )}
            </SectionCard>
          )}

          {/* ── APPOINTMENTS ── */}
          {activeTab === TAB_APPOINTMENTS && (
            <>
              <SectionCard title="📅 Upcoming">
                {upcomingAppts.length === 0
                  ? <EmptyState icon="calendar" title="Nothing scheduled"
                      message="Upcoming doctor visits will appear here." />
                  : upcomingAppts.map(a => <AppointmentRow key={a.id} appointment={a} />)}
              </SectionCard>

              {pastAppts.length > 0 && (
                <SectionCard title="🕓 Past">
                  {(showPastAppointments ? pastAppts : pastAppts.slice(0, 5))
                    .map(a => <AppointmentRow key={a.id} appointment={a} />)}
                  {pastAppts.length > 5 && (
                    <div style={{ paddingTop: 14 }}>
                      <button type="button" className="btn btn-ghost btn-sm"
                        onClick={() => setShowPastAppointments(v => !v)}>
                        {showPastAppointments
                          ? 'Show fewer'
                          : `Show all ${pastAppts.length} past appointments`}
                      </button>
                    </div>
                  )}
                </SectionCard>
              )}
            </>
          )}

          {/* ── NOTES ──
              30 Jul 2026 — Enhancement #4: Navigator/Doctor notes visible to
              the subscriber. Read-only — subscribers can view but not add
              notes, same as they can view but not create appointments. */}
          {activeTab === TAB_NOTES && (
            <SectionCard title="📝 Care Team Notes">
              {sortedNotes.length === 0
                ? <EmptyState icon="note" title="No notes yet"
                    message="Notes from your Navigator or Doctor will appear here." />
                : sortedNotes.map(n => <NoteRow key={n.id} note={n} />)}
            </SectionCard>
          )}
        </>
      )}

      {autoTriage.stage === 'AWAITING_IDENTITY_CONFIRMATION' && (
        <IdentityMismatchModal
          mismatch={autoTriage.identityMismatch}
          onConfirm={handleConfirmUploadIdentity}
          onCancel={handleCancelUploadIdentity}
        />
      )}
    </SubscriberAppShell>
  );
}
